import { EpaperPublicationType, Issue } from '@news-mono/web-common'
import { TheWestSections } from '@west-australian-newspapers/publication-types'
import React from 'react'
import {
    StyledDate,
    StyledDateSpan,
    StyledEpaperCollectionPaperContainer,
    StyledEpaperCollectionPaperMainContainer,
    StyledEpaperCollectionPaperSpanText,
    StyledEpaperCollectionPapersStyledImg,
    StyledEpaperCollectionPaperWrapper,
    StyledIconPremium,
} from './EpaperCollection.styled'

export interface LatestEditionPapersProps {
    issues: Issue[]
    sectionToExclude?: EpaperPublicationType
}

export const EpaperCollection: React.FC<LatestEditionPapersProps> = (props) => {
    let issues = props.issues
    if (props.sectionToExclude)
        issues = issues.filter(
            (issue) => issue.publicationName !== props.sectionToExclude,
        )

    return (
        <StyledEpaperCollectionPaperMainContainer>
            {issues.map((issue, idx) => {
                const mastheadMetadata =
                    issue.publicationName &&
                    getMastheadMetadataFromMastheadName(issue.publicationName)

                const ePaperRedirectUrl =
                    issue.publicationName !== undefined
                        ? getMastheadMetadataFromMastheadName(
                              issue.publicationName,
                          )?.ePaperUrl
                        : issue.url

                return (
                    issue && (
                        <StyledEpaperCollectionPaperContainer key={idx}>
                            <a href={ePaperRedirectUrl} target="_blank">
                                <StyledEpaperCollectionPapersStyledImg
                                    src={issue.coverUrl}
                                />
                            </a>
                            <StyledEpaperCollectionPaperWrapper>
                                {mastheadMetadata !== undefined ? (
                                    <>
                                        {issue.isPremium && (
                                            <StyledIconPremium />
                                        )}
                                        <StyledEpaperCollectionPaperSpanText>
                                            {mastheadMetadata.mastheadName}
                                        </StyledEpaperCollectionPaperSpanText>
                                    </>
                                ) : (
                                    <>
                                        <StyledIconPremium />
                                        <StyledEpaperCollectionPaperSpanText>
                                            <FormattedDate date={issue.date} />
                                        </StyledEpaperCollectionPaperSpanText>
                                    </>
                                )}
                            </StyledEpaperCollectionPaperWrapper>
                        </StyledEpaperCollectionPaperContainer>
                    )
                )
            })}
        </StyledEpaperCollectionPaperMainContainer>
    )
}

const FormattedDate: React.FC<{ date: Date }> = (props) => {
    const day = getFormattedDay(props.date)
    const date = getFormattedDate(props.date)

    return (
        <>
            <StyledDate>
                <StyledDateSpan>{day}, </StyledDateSpan>
                <StyledDateSpan>{date}</StyledDateSpan>
            </StyledDate>
        </>
    )
}

function getFormattedDate(date: Date): string {
    const fullDate = date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })
    return fullDate
}

function getFormattedDay(date: Date): string {
    const todaysDate = new Date()
    const day =
        todaysDate.getDate() - date.getDate() === 0
            ? 'Today'
            : date.toLocaleDateString('en-AU', {
                  weekday: 'long',
              })
    return day
}

function getMastheadMetadataFromMastheadName(masthead: string) {
    type MastheadTypes = TheWestSections & {
        readonly perthnow: 'perthnow'
        readonly thewest: 'thewest'
    }
    const mastheadToEPaperUrlMapping: Map<
        keyof MastheadTypes,
        { mastheadName: string; ePaperUrl: string }
    > = new Map([
        [
            'default',
            {
                ePaperUrl: 'https://thewest.com.au/epaper',
                mastheadName: 'The West Australian',
            },
        ],
        [
            'albany-advertiser',
            {
                ePaperUrl: 'https://www.albanyadvertiser.com.au/epaper',
                mastheadName: 'Albany Advertiser',
            },
        ],
        [
            'broome-advertiser',
            {
                ePaperUrl: 'https://www.broomead.com.au/epaper',
                mastheadName: 'Broome Advertiser',
            },
        ],
        [
            'bunbury-herald',
            {
                ePaperUrl: 'https://www.bunburyherald.com.au/epaper',
                mastheadName: 'Bunbury Herald',
            },
        ],
        [
            'geraldton-guardian',
            {
                ePaperUrl: 'https://www.geraldtonguardian.com.au/epaper',
                mastheadName: 'Geraldton Guardian',
            },
        ],
        [
            'great-southern-herald',
            {
                ePaperUrl: 'https://www.gsherald.com.au/epaper',
                mastheadName: 'Great Southern Herald',
            },
        ],
        [
            'kalgoorlie-miner',
            {
                ePaperUrl: 'https://www.kalminer.com.au/epaper',
                mastheadName: 'Kalgoorlie Miner',
            },
        ],
        [
            'the-kimberley-echo',
            {
                ePaperUrl: 'https://www.kimberleyecho.com.au/epaper',
                mastheadName: 'The Kimberley Echo',
            },
        ],
        [
            'manjimup-bridgetown-times',
            {
                ePaperUrl: 'https://www.mbtimes.com.au/epaper',
                mastheadName: 'Manjimup-Bridgetown Times',
            },
        ],
        [
            'midwest-times',
            {
                ePaperUrl: 'https://www.midwesttimes.com.au/epaper',
                mastheadName: 'Midwest Times',
            },
        ],
        [
            'narrogin-observer',
            {
                ePaperUrl: 'https://www.narroginobserver.com.au/epaper',
                mastheadName: 'Narrogin Observer',
            },
        ],
        [
            'pilbara-news',
            {
                ePaperUrl: 'https://www.pilbaranews.com.au/epaper',
                mastheadName: 'Pilbara News',
            },
        ],
        [
            'sound-telegraph',
            {
                ePaperUrl: 'https://www.soundtelegraph.com.au/epaper',
                mastheadName: 'Sound Telegraph',
            },
        ],
        [
            'south-western-times',
            {
                ePaperUrl: 'https://www.swtimes.com.au/epaper',
                mastheadName: 'South Western Times',
            },
        ],
        [
            'busselton-dunsborough-times',
            {
                ePaperUrl: 'https://www.bdtimes.com.au/epaper',
                mastheadName: 'Busselton Dunsborough Times',
            },
        ],
        [
            'countryman',
            {
                ePaperUrl: 'https://www.countryman.com.au/epaper',
                mastheadName: 'Countryman',
            },
        ],
        [
            'augusta-margaret-river-times',
            {
                ePaperUrl: 'https://www.amrtimes.com.au/epaper',
                mastheadName: 'Augusta Margret River Times',
            },
        ],
        [
            'harvey-waroona-reporter',
            {
                ePaperUrl: 'https://www.harveyreporter.com.au/epaper',
                mastheadName: 'Harvey-Waroona Reporter',
            },
        ],
        [
            'north-west-telegraph',
            {
                ePaperUrl: 'https://www.northwesttelegraph.com.au/epaper',
                mastheadName: 'North West Telegraph',
            },
        ],
        [
            'perthnow',
            {
                ePaperUrl: 'https://www.perthnow.com.au/epaper',
                mastheadName: 'Perthnow',
            },
        ],
    ])

    return mastheadToEPaperUrlMapping.get(masthead as keyof MastheadTypes)
}

/**
 *
The West Australian (https://thewest.com.au/epaper) [premium]

PerthNow (https://www.perthnow.com.au/epaper)

Albany Advertiser (https://www.albanyadvertiser.com.au/epaper) [premium]

Augusta Margret River Times (https://www.amrtimes.com.au/epaper)

Broome Advertiser (https://www.broomead.com.au/epaper)

Bunbury Herald (https://www.bunburyherald.com.au/epaper)

Busselton Dunsborough Times (https://www.bdtimes.com.au/epaper)

Countryman (https://www.countryman.com.au/epaper) [premium]

Geraldton Guardian (https://www.geraldtonguardian.com.au/epaper) [premium]

Great Southern Herald (https://www.gsherald.com.au/epaper) [premium]

Harvey-Waroona Reporter (https://www.harveyreporter.com.au/epaper)

Kalgoorlie Miner (https://www.kalminer.com.au/epaper) [premium]

The Kimberley Echo (https://www.kimberleyecho.com.au/epaper) [premium]

Manjimup-Bridgetown Times (https://www.mbtimes.com.au/epaper) [premium]

Midwest Times (https://www.midwesttimes.com.au/epaper)

Narrogin Observer (https://www.narroginobserver.com.au/epaper) [premium]

North West Telegraph (https://www.northwesttelegraph.com.au/epaper)

Pilbara News (https://www.pilbaranews.com.au/epaper)

Sound Telegraph (https://www.soundtelegraph.com.au/epaper)

South Western Times (https://www.swtimes.com.au/epaper) [premium]
*/
